import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { sendToUnityWithResponse } from "./Unityboards/UnityBridge";
import { normalizeUnityIframeMessageData } from "../services/editor/spaceNavigationEvents";
import {
  applyPwaUpdate,
  clearPendingPwaUpdate,
  getPendingPwaUpdate,
  PWA_UPDATE_AVAILABLE_EVENT,
  PwaUpdateEventDetail,
} from "../pwaUpdate";

const UNITY_UNLOAD_TIMEOUT_MS = 5_000;

export const unloadMobileViewerForUpdate = async (): Promise<void> => {
  const iframe = document.getElementById(
    "unity-iframe",
  ) as HTMLIFrameElement | null;
  if (!iframe?.contentWindow) return;

  await new Promise<void>((resolve) => {
    let settled = false;
    let timeout = 0;
    function cleanup() {
      window.clearTimeout(timeout);
      window.removeEventListener("message", onMessage);
    }
    function finish() {
      if (settled) return;
      settled = true;
      cleanup();
      resolve();
    }
    function onMessage(event: MessageEvent) {
      const data = normalizeUnityIframeMessageData(event.data);
      if (
        event.source !== iframe.contentWindow ||
        data?.type !== "unityToReact_unloadComplete"
      ) {
        return;
      }
      finish();
    }
    timeout = window.setTimeout(finish, UNITY_UNLOAD_TIMEOUT_MS);
    window.addEventListener("message", onMessage);

    void sendToUnityWithResponse("reactToUnity_unloadViewR", {
      reason: "pwa_update",
    }).catch(() => finish());
  });
};

export default function PwaUpdateCoordinator() {
  const location = useLocation();

  useEffect(() => {
    const applyPendingUpdate = () => {
      const pendingUpdate = getPendingPwaUpdate();
      if (!pendingUpdate) return;
      // Desktop Editor may own unsaved scene state. Its existing save/persist
      // orchestration must decide when an update can reload the app.
      if (location.pathname === "/editor") return;

      const beforeReload = location.pathname.startsWith("/mobile/viewer/")
        ? unloadMobileViewerForUpdate
        : undefined;
      void applyPwaUpdate({
        beforeReload,
        buildId: pendingUpdate.buildId,
      })
        .then((applied) => {
          if (applied) clearPendingPwaUpdate();
        })
        .catch((error) => {
          console.warn(
            "App update paused. Finish the current task and try again.",
            error,
          );
        });
    };
    const onUpdateAvailable = (
      _event: CustomEvent<PwaUpdateEventDetail>,
    ) => applyPendingUpdate();

    window.addEventListener(
      PWA_UPDATE_AVAILABLE_EVENT,
      onUpdateAvailable as EventListener,
    );
    applyPendingUpdate();
    return () =>
      window.removeEventListener(
        PWA_UPDATE_AVAILABLE_EVENT,
        onUpdateAvailable as EventListener,
      );
  }, [location.pathname]);

  return null;
}
